import React, { useMemo } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { DialogOverlay, Paragraph, Grid, Cell, Separator, Alert } from '@fs/zion-ui'
import { useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import { useFeatureFlag } from '@fs/zion-flags'
import { useOverlayContext } from '../../../../shared/providers/overlayContextProvider/OverlayContext'
import SharedDialogFooter from './SharedDialogFooter'
import { getFieldValue } from '../panels/sharedHighlightPanel/utils/SharedHighlightUtils'
import { getLinkedFields } from '../panels/sharedHighlightPanel/components/reassign/reassignHighlightUtils'

// Helper function to group fields by label
const groupFieldsByLabel = (fields, state, t) => {
  return fields.reduce((acc, field) => {
    const label = field?.label || field?.displayLabel || 'New Field'
    if (!acc[label]) acc[label] = []
    const fieldValue = getFieldValue(field, state) || t('edit-shared-highlight.no-value', '—')
    acc[label].push(fieldValue)
    return acc
  }, {})
}

// Simple view component
const SimpleView = ({ t, overlay, handleClick }) => (
  <DialogOverlay {...overlay} title={t('edit-highlight.used-by-other-field.title', 'Review then add')}>
    {t(
      'edit-highlight.used-by-other-field.message',
      'This highlight is used by another name. Do you wish to review the fields using this highlight and then add?'
    )}
    <SharedDialogFooter
      onCancel={() => handleClick({ action: 'cancel' })}
      onReview={() => handleClick({ action: 'review' })}
    />
  </DialogOverlay>
)

// Detailed view component
const DetailedView = ({ t, overlay, handleClick, h, groupedFields }) => {
  const currentFieldLabel = h.activeFieldInfo?.displayLabel || h.activeFieldInfo?.label || 'Current Field'
  const currentFieldValue = h.activeFieldInfo?.fieldValues?.[0]?.origValue?.text || ''

  return (
    <DialogOverlay {...overlay} title={t('edit-shared-highlight.reassign-title', 'Reassign the Highlight')}>
      <Paragraph size="sm">
        {t(
          'edit-shared-highlight.reassign-description',
          'The highlight is about to move from the assigned field to another:'
        )}
      </Paragraph>

      <Separator size="nano" />

      <Grid gutters="sm">
        <Cell columns={6}>
          {t('edit-shared-highlight.assigned', 'Assigned')}
          {Object.entries(groupedFields).map(([label, values]) => (
            <Paragraph size="sm" key={label}>
              <strong>{label}:</strong> {values.join(', ')}
            </Paragraph>
          ))}
        </Cell>
        <Cell columns={6}>
          {t('edit-shared-highlight.reassigned', 'Reassigned')}
          <Paragraph size="sm">
            <strong>{currentFieldLabel}:</strong> {currentFieldValue || t('edit-shared-highlight.no-value', '—')}
          </Paragraph>
        </Cell>
      </Grid>

      <Separator size="nano" />

      <Alert type="warning">
        {t(
          'edit-shared-highlight.reassign-warning',
          'Reassigning this highlight will remove it from the current field.'
        )}
      </Alert>

      <Separator size="nano" />

      <Trans i18nKey="edit-shared-highlight.select-review">
        <Paragraph size="sm">
          Select <strong>Review</strong> to start the move.
        </Paragraph>
      </Trans>

      <SharedDialogFooter
        onCancel={() => handleClick({ action: 'cancel' })}
        onReview={() => handleClick({ action: 'review' })}
      />
    </DialogOverlay>
  )
}

const SharedHighlightOverlay = ({ overlay }) => {
  const [t] = useTranslation()
  const { openOverlay } = useOverlayContext()
  const h = useSlsHighlightsContext()
  const state = useSlsStateContext()
  const reassignHighlightFlow = useFeatureFlag('rip_sharedImageViewer_reassignHighlightFlow')
  const { getHighlights, activeFieldInfo } = h

  const linkedFields = useMemo(() => {
    return getLinkedFields(getHighlights, state.images, activeFieldInfo)
  }, [getHighlights, state.images, activeFieldInfo])

  const handleClick = ({ action }) => {
    if (action === 'review') {
      openOverlay('editSharedHighlightPage1')
    } else {
      h.setEditMode(true)
    }
    overlay.close()
  }

  // Simple view when flag is off
  if (!reassignHighlightFlow.isOn) {
    return <SimpleView t={t} overlay={overlay} handleClick={handleClick} />
  }

  const groupedFields = groupFieldsByLabel(linkedFields, state, t)

  return <DetailedView t={t} overlay={overlay} handleClick={handleClick} h={h} groupedFields={groupedFields} />
}

export default SharedHighlightOverlay
